import { CitationWithId } from '@sciflow/cite';
import { normalizeText } from '../utils/normalize';

interface ProseMirrorJsonNode {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: ProseMirrorJsonNode[];
}

export interface CitationOccurrence {
  partId: string;
  title: string;
}

interface WalkContext {
  citationsById: Map<string, CitationWithId>;
}

/**
 * Builds a reference-to-part occurrence map from a ProseMirror document snapshot.
 */
export const buildCitationOccurrencesByReferenceId = (
  doc: ProseMirrorJsonNode | null | undefined,
  citations: CitationWithId[] = [],
): Map<string, CitationOccurrence[]> => {
  const occurrencesByReferenceId = new Map<string, CitationOccurrence[]>();
  const occurrenceKeysByReferenceId = new Map<string, Set<string>>();
  const context: WalkContext = { citationsById: buildCitationsById(citations) };

  const addOccurrence = (referenceId: string | undefined, occurrence: CitationOccurrence) => {
    const normalizedReferenceId = normalizeText(referenceId);
    if (!normalizedReferenceId) {
      return;
    }

    const key = `${occurrence.partId}::${occurrence.title}`;
    const existingKeys = occurrenceKeysByReferenceId.get(normalizedReferenceId) || new Set<string>();
    if (existingKeys.has(key)) {
      return;
    }

    existingKeys.add(key);
    occurrenceKeysByReferenceId.set(normalizedReferenceId, existingKeys);

    const existing = occurrencesByReferenceId.get(normalizedReferenceId) || [];
    occurrencesByReferenceId.set(normalizedReferenceId, [...existing, occurrence]);
  };

  const walk = (
    node: ProseMirrorJsonNode | null | undefined,
    currentPart: CitationOccurrence | null,
  ): void => {
    if (!node) {
      return;
    }

    const nodeType = normalizeText(node.type);
    if (nodeType === 'part') {
      const part = buildPartOccurrence(node);
      const nextPart = isBibliographyPart(node) ? null : part;
      for (const child of node.content || []) {
        walk(child, nextPart);
      }
      return;
    }

    if (nodeType === 'citation' && currentPart) {
      const referenceIds = getCitationReferenceIds(node, context.citationsById);
      for (const referenceId of referenceIds) {
        addOccurrence(referenceId, currentPart);
      }
    }

    for (const child of node.content || []) {
      walk(child, currentPart);
    }
  };

  walk(doc, null);
  return occurrencesByReferenceId;
};

/**
 * Builds a map of reference IDs to their first appearance index in document order.
 * Includes citation and managed reference nodes while ignoring bibliography parts.
 */
export const buildReferenceAppearanceIndexById = (
  doc: ProseMirrorJsonNode | null | undefined,
  citations: CitationWithId[] = [],
): Map<string, number> => {
  const appearanceByReferenceId = new Map<string, number>();
  const context: WalkContext = { citationsById: buildCitationsById(citations) };
  let nextIndex = 0;

  const addAppearance = (referenceId: string | undefined) => {
    const normalizedReferenceId = normalizeText(referenceId);
    if (!normalizedReferenceId || appearanceByReferenceId.has(normalizedReferenceId)) {
      return;
    }

    appearanceByReferenceId.set(normalizedReferenceId, nextIndex);
    nextIndex += 1;
  };

  const walk = (node: ProseMirrorJsonNode | null | undefined, include: boolean): void => {
    if (!node) {
      return;
    }

    const nodeType = normalizeText(node.type);
    if (nodeType === 'part') {
      const nextInclude = include && !isBibliographyPart(node);
      for (const child of node.content || []) {
        walk(child, nextInclude);
      }
      return;
    }

    if (include && nodeType === 'citation') {
      const referenceIds = getCitationReferenceIds(node, context.citationsById);
      for (const referenceId of referenceIds) {
        addAppearance(referenceId);
      }
    }

    if (include && nodeType === 'reference') {
      addAppearance(typeof node.attrs?.refId === 'string' ? node.attrs.refId : undefined);
    }

    for (const child of node.content || []) {
      walk(child, include);
    }
  };

  walk(doc, true);
  return appearanceByReferenceId;
};

/**
 * Builds the chapter/part occurrence label from a part node.
 */
const buildPartOccurrence = (partNode: ProseMirrorJsonNode): CitationOccurrence => {
  const partId = normalizeText(partNode.attrs?.id) || 'unknown-part';
  const title = getPartTitle(partNode) || 'Untitled chapter';
  return { partId, title };
};

/**
 * Resolves the part title from the first level-1 heading.
 */
const getPartTitle = (partNode: ProseMirrorJsonNode): string | undefined => {
  const firstHeading = (partNode.content || []).find(
    (child) => normalizeText(child.type) === 'heading' && Number(child.attrs?.level) === 1,
  );

  return normalizeText(extractTextContent(firstHeading));
};

/**
 * Determines whether a part is a bibliography container.
 */
const isBibliographyPart = (partNode: ProseMirrorJsonNode): boolean => {
  const firstChild = (partNode.content || [])[0];
  const firstChildType = normalizeText(firstChild?.type);
  if (firstChildType === 'heading' && Number(firstChild?.attrs?.level) === 1) {
    const headingType = normalizeText(firstChild?.attrs?.type);
    if (headingType === 'bibliography') {
      return true;
    }
  }

  return normalizeText(partNode.attrs?.type) === 'bibliography';
};

/**
 * Builds a lookup of citation objects by citation ID.
 */
const buildCitationsById = (citations: CitationWithId[]): Map<string, CitationWithId> => {
  const byId = new Map<string, CitationWithId>();
  for (const citation of citations || []) {
    const citationId = normalizeText(citation?.citationID);
    if (!citationId) {
      continue;
    }
    byId.set(citationId, citation);
  }
  return byId;
};

/**
 * Extracts all reference IDs represented by one citation node.
 */
const getCitationReferenceIds = (
  citationNode: ProseMirrorJsonNode,
  citationsById: Map<string, CitationWithId>,
): string[] => {
  const sourceValue = normalizeText(citationNode.attrs?.source);
  if (sourceValue) {
    const fromSource = parseCitationSourceIds(sourceValue);
    if (fromSource.length > 0) {
      return fromSource;
    }
  }

  const citationNodeId = normalizeText(citationNode.attrs?.id);
  if (!citationNodeId) {
    return [];
  }

  const citation = citationsById.get(citationNodeId);
  if (!citation) {
    return [];
  }

  return getReferenceIdsFromCitation(citation);
};

/**
 * Parses encoded citation source payload and extracts reference IDs.
 */
const parseCitationSourceIds = (source: string): string[] => {
  try {
    const decoded = decodeURIComponent(source);
    const parsed = JSON.parse(decoded);
    if (!Array.isArray(parsed)) {
      return [];
    }

    const ids = parsed
      .flatMap((item) => [
        normalizeText((item as Record<string, unknown>)?.id),
        normalizeText((item as Record<string, unknown>)?.itemData?.['id']),
      ])
      .filter((id): id is string => Boolean(id));

    return Array.from(new Set(ids));
  } catch (_error) {
    return [];
  }
};

/**
 * Extracts all reference IDs from a citation object.
 */
const getReferenceIdsFromCitation = (citation: CitationWithId): string[] => {
  const ids = (citation?.citationItems || [])
    .flatMap((item) => [normalizeText(item?.id), normalizeText(item?.itemData?.id)])
    .filter((id): id is string => Boolean(id));

  return Array.from(new Set(ids));
};

/**
 * Collects text recursively from a ProseMirror JSON node.
 */
const extractTextContent = (node: ProseMirrorJsonNode | undefined): string => {
  if (!node) {
    return '';
  }

  const own = normalizeText(node.attrs?.label) || normalizeText((node as Record<string, unknown>)?.text) || '';
  const children = (node.content || []).map((child) => extractTextContent(child)).join('');
  return `${own}${children}`;
};
